import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Button,
  Drawer,
  Group,
  NumberInput,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconChefHat,
  IconPlus,
  IconSearch,
  IconShoppingCart,
  IconTrash,
} from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import { useAuthStore } from '../../auth/authStore';
import { useBranches } from '../branches/branchesApi';
import { useItems } from '../items/itemsApi';
import type { CustomerDto } from '../customers/types';
import type { ItemDto } from '../items/types';
import { useSubmitPhoneOrder } from './callCenterApi';
import type { PhoneOrderLine } from './types';

interface Props {
  opened: boolean;
  onClose: () => void;
  customer: CustomerDto;
}

const ORDER_TYPE_DELIVERY = 3;
const STATUS_PLACED = 2;

// Call-Center order drawer. Agent picks branch + items, server
// receives a Placed order with OrderType=Delivery + the customer
// attached. KDS gets the ticket via the normal routing flow.
// Payment happens later — phone orders are typically COD or pre-paid
// via a separate channel; settling is the cashier's problem.

export function PhoneOrderDrawer({ opened, onClose, customer }: Props) {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const submit = useSubmitPhoneOrder();
  const branchesQuery = useBranches();
  const itemsQuery = useItems();

  const branches = branchesQuery.data ?? [];
  const items = itemsQuery.data ?? [];

  const [branchId, setBranchId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [lines, setLines] = useState<PhoneOrderLine[]>([]);

  // Auto-pick the first branch the moment branches load — same UX as
  // the floor plan / stations pages.
  useEffect(() => {
    if (!branchId && branches.length > 0) {
      setBranchId(branches[0]!.branchId);
    }
  }, [branchId, branches]);

  useEffect(() => {
    if (!opened) {
      setLines([]);
      setSearch('');
    }
  }, [opened]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items.slice(0, 50);
    return items
      .filter(
        (i) =>
          i.nameAr.toLowerCase().includes(q) ||
          i.nameEn?.toLowerCase().includes(q) ||
          i.sku.toLowerCase().includes(q),
      )
      .slice(0, 50);
  }, [items, search]);

  const subtotal = lines.reduce((s, l) => s + l.quantity * l.unitPriceSnapshot, 0);

  const addLine = (item: ItemDto) => {
    setLines((prev) => {
      const existing = prev.find((l) => l.itemId === item.itemId);
      if (existing) {
        return prev.map((l) =>
          l.cartLineId === existing.cartLineId ? { ...l, quantity: l.quantity + 1 } : l,
        );
      }
      return [
        ...prev,
        {
          cartLineId: crypto.randomUUID(),
          itemId: item.itemId,
          itemNameSnapshot: item.nameAr,
          unitPriceSnapshot: item.basePrice,
          quantity: 1,
        },
      ];
    });
  };

  const updateQty = (cartLineId: string, quantity: number) =>
    setLines((prev) =>
      prev
        .map((l) => (l.cartLineId === cartLineId ? { ...l, quantity } : l))
        .filter((l) => l.quantity > 0),
    );

  const removeLine = (cartLineId: string) =>
    setLines((prev) => prev.filter((l) => l.cartLineId !== cartLineId));

  const sendToKitchen = async () => {
    if (!branchId || !user || lines.length === 0) return;
    const now = new Date().toISOString();
    try {
      await submit.mutateAsync({
        orderId: crypto.randomUUID(),
        localOrderNumber: `#CC-${Date.now().toString().slice(-6)}`,
        branchId,
        cashierUserId: user.userId,
        shiftId: null,
        customerId: customer.customerId,
        orderType: ORDER_TYPE_DELIVERY,
        status: STATUS_PLACED,
        subtotal,
        taxTotal: 0,
        discountTotal: 0,
        total: subtotal,
        openedAt: now,
        placedAt: now,
        paidAt: null,
        voidedAt: null,
        voidReason: null,
        restaurantTableId: null,
        lines: lines.map((l) => ({
          orderLineId: l.cartLineId,
          itemId: l.itemId,
          itemNameSnapshot: l.itemNameSnapshot,
          unitPriceSnapshot: l.unitPriceSnapshot,
          quantity: l.quantity,
          lineDiscount: 0,
          notes: null,
          modifiers: [],
          courseNumber: 1,
        })),
        payments: [],
      });
      notifications.show({
        color: 'green',
        title: t('callCenter.order.successTitle'),
        message: customer.fullName,
      });
      onClose();
    } catch (err) {
      const msg = isAxiosError(err)
        ? ((err.response?.data as { detail?: string } | undefined)?.detail ?? err.message)
        : t('common.toasts.errorBody');
      notifications.show({
        color: 'red',
        title: t('callCenter.order.failTitle'),
        message: msg,
      });
    }
  };

  const branchOptions = branches.map((b) => ({
    value: b.branchId,
    label: i18n.language === 'ar' ? b.nameAr : (b.nameEn ?? b.nameAr),
  }));

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={600}>
          {t('callCenter.order.title')} — {customer.fullName}
        </Text>
      }
      position={i18n.language === 'ar' ? 'left' : 'right'}
      size="xl"
      padding="lg"
    >
      <Stack gap="md">
        <Select
          label={t('callCenter.order.branchLabel')}
          data={branchOptions}
          value={branchId}
          onChange={setBranchId}
          searchable
          withAsterisk
          disabled={branchesQuery.isLoading || branches.length === 0}
        />

        <TextInput
          placeholder={t('callCenter.order.itemSearchPlaceholder')}
          leftSection={<IconSearch size={16} stroke={1.6} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />

        <ScrollArea h={220}>
          <Table verticalSpacing="xs" striped="even">
            <Table.Tbody>
              {filteredItems.map((it) => (
                <Table.Tr key={it.itemId}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {it.nameAr}
                    </Text>
                    <Text size="xs" c="dimmed" className="font-mono">
                      {it.sku}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="end" className="font-numeric">
                    {it.basePrice.toFixed(2)}
                  </Table.Td>
                  <Table.Td style={{ width: 60 }}>
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconPlus size={12} stroke={1.8} />}
                      onClick={() => addLine(it)}
                    >
                      {t('callCenter.order.add')}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
              {filteredItems.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={3}>
                    <Text size="sm" c="dimmed" ta="center" py="md">
                      {t('callCenter.order.noItems')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>

        <Alert color="gray" variant="light" icon={<IconShoppingCart size={16} stroke={1.6} />}>
          {t('callCenter.order.cartLines', { count: lines.length })} ·{' '}
          <span className="font-numeric">{subtotal.toFixed(2)}</span>
        </Alert>

        {lines.length > 0 && (
          <Stack gap="xs">
            {lines.map((l) => (
              <Group
                key={l.cartLineId}
                justify="space-between"
                px="sm"
                py={6}
                className="rounded-md border border-[var(--wheels-border)] bg-white"
              >
                <div className="flex-1 min-w-0">
                  <Text size="sm" fw={500} lineClamp={1}>
                    {l.itemNameSnapshot}
                  </Text>
                </div>
                <Group gap="xs">
                  <NumberInput
                    value={l.quantity}
                    onChange={(v) =>
                      updateQty(l.cartLineId, typeof v === 'number' ? v : 0)
                    }
                    min={0}
                    step={1}
                    size="xs"
                    style={{ width: 80 }}
                  />
                  <Text size="sm" fw={600} className="font-numeric" style={{ width: 60, textAlign: 'end' }}>
                    {(l.quantity * l.unitPriceSnapshot).toFixed(2)}
                  </Text>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    onClick={() => removeLine(l.cartLineId)}
                    aria-label="remove"
                  >
                    <IconTrash size={14} stroke={1.6} />
                  </ActionIcon>
                </Group>
              </Group>
            ))}
          </Stack>
        )}

        <Group justify="flex-end" gap="sm" mt="sm">
          <Button variant="default" onClick={onClose} disabled={submit.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            color="wheels"
            leftSection={<IconChefHat size={16} stroke={1.6} />}
            loading={submit.isPending}
            disabled={!branchId || lines.length === 0}
            onClick={sendToKitchen}
          >
            {t('callCenter.order.send')}
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
